import { FiCheck, FiZap } from "react-icons/fi";

interface WorkoutPlanAIModalFooterProps {
  readonly hasResult: boolean;
  readonly aiLoading: boolean;
  readonly description: string;
  readonly visiblePrescriptionsCount: number;
  readonly totalExercises: number;
  readonly onClose: () => void;
  readonly onAnalyze: () => void;
  readonly onBack: () => void;
  readonly onApply: () => void;
}

export function WorkoutPlanAIModalFooter({
  hasResult,
  aiLoading,
  description,
  visiblePrescriptionsCount,
  totalExercises,
  onClose,
  onAnalyze,
  onBack,
  onApply,
}: WorkoutPlanAIModalFooterProps) {
  if (!hasResult) {
    return (
      <div className="sticky bottom-0 border-t border-stroke bg-white/95 px-4 sm:px-8 py-4 sm:py-6 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:border-strokedark dark:bg-boxdark/95 dark:supports-[backdrop-filter]:bg-boxdark/80">
        <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          {description.trim()
            ? "A IA vai analisar sua descrição e criar treinos com exercícios da base."
            : "Preencha uma descrição para habilitar a análise com IA."}
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto min-h-[48px] rounded-xl border border-stroke px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800/50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onAnalyze}
            disabled={aiLoading || !description.trim()}
            className="w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-sm transition-all hover:shadow-xl disabled:opacity-50"
          >
            {aiLoading ? (
              <>
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Criando Treino...
              </>
            ) : (
              <>
                <FiZap size={18} />
                Criar Treino
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sticky bottom-0 border-t border-stroke bg-white/95 px-4 sm:px-8 py-4 sm:py-6 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:border-strokedark dark:bg-boxdark/95 dark:supports-[backdrop-filter]:bg-boxdark/80">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-gray-100 px-3 py-1 font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          {visiblePrescriptionsCount} treinos prontos
        </span>
        <span className="rounded-full bg-gray-100 px-3 py-1 font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          {totalExercises} exercícios no total
        </span>
        {visiblePrescriptionsCount === 0 && (
          <span className="text-yellow-700 dark:text-yellow-400">
            Nada será aplicado enquanto não houver treinos visíveis.
          </span>
        )}
      </div>
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
        <button
          type="button"
          onClick={onBack}
          className="w-full sm:w-auto min-h-[48px] rounded-xl border border-stroke px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800/50"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={onApply}
          disabled={visiblePrescriptionsCount === 0}
          className="w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-sm transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiCheck size={18} />
          Aplicar Treinos{" "}
          {visiblePrescriptionsCount > 0 && `(${visiblePrescriptionsCount})`}
        </button>
      </div>
    </div>
  );
}
